import { computed, inject, Service, signal } from "@angular/core";
import { Subject } from "rxjs";

import { AdaaHelper } from "../../core/utils";
import { Constants } from "../constants/constants";
import { AdaaBoolean } from "../constants/enums";
import type { CycleModelType, EntityModelType } from "../models";
import { ConsoleService } from "./console-service";
import { CyclesApiService } from "./cycles-api-service";
import { DataTableService } from "./data-table-service";
import { EntitiesApiService } from "./entities-api-service";
import { FilesApiService } from "./files-api-service";
import { PermissionsService } from "./permissions-service";
import { TabsService } from "./tabs-service";

@Service()
export class SystemLayoutService {
  private readonly _entitiesApiService = inject(EntitiesApiService);
  private readonly _cyclesApiService = inject(CyclesApiService);
  private readonly _filesApiService = inject(FilesApiService);
  private readonly _permissionService = inject(PermissionsService);
  private readonly _consoleService = inject(ConsoleService);
  private readonly _dataTableService = inject(DataTableService);
  private readonly _tabsService = inject(TabsService);

  private _cycleHasChanged = new Subject<void>();
  hasCycleChanged$ = this._cycleHasChanged.asObservable();

  private _activeEntityHasChanged = new Subject<void>();
  hasActiveEntityChanged$ = this._activeEntityHasChanged.asObservable();

  cycles = signal<CycleModelType[]>([]);
  entities = signal<EntityModelType[]>([]);
  activeCycle = signal<CycleModelType | null>(null);
  activeEntity = signal<EntityModelType | null>(null);
  activeEntityLogo = signal<string | undefined>("assets/images/adaa-pmo-logo.png");
  searchTerm = signal<string | null>(null);

  isReady = computed(() => {
    const hasCycles = Array.isArray(this.cycles()) && this.cycles().length > 0;
    const hasEntities = Array.isArray(this.entities()) && this.entities().length > 0;

    if (
      !!this.activeEntity() &&
      (this.activeEntity()?.entityType === Constants.ENTITY_TYPES.EXTERNAL ||
        this.activeEntity()?.entityType === Constants.ENTITY_TYPES.PARTICIPATING)
    ) {
      return hasEntities;
    }

    return hasCycles && !!this.activeCycle() && hasEntities && !!this.activeEntity();
  });

  public get entity() {
    if (this.activeEntity()) {
      return this.activeEntity();
    }
    return AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      type: "json",
    }) as EntityModelType;
  }

  public get cycle() {
    if (this.activeCycle()) {
      return this.activeCycle();
    }
    return AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, {
      type: "json",
    }) as CycleModelType;
  }

  public selectEntity(entity: EntityModelType, preventDefault = false) {
    const oldEntity = AdaaHelper.clone(this.entity);

    this.activeEntity.update(() => entity);
    this._downloadEntityLogo();
    AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentEntity, entity, "json");

    this._fetchAllCycles();
    this._consoleService.init(false);

    //Only Emit if the entity id is changed
    if (entity.id === oldEntity.id) return;
    if (
      entity.entityType !== Constants.ENTITY_TYPES.EXTERNAL &&
      entity.entityType !== Constants.ENTITY_TYPES.PARTICIPATING
    )
      this._initCycles();

    if (!preventDefault) {
      //Reset the tables filters
      this._dataTableService.resetAllTablesState();

      //Reload all active tabs
      this._tabsService.reloadOtherTabs();

      this._activeEntityHasChanged.next();
    }
  }

  public selectCycle(cycle: CycleModelType, preventDefault = false) {
    const oldCycle = AdaaHelper.clone(this.cycle);
    this.activeCycle.update(() => cycle);

    AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentPlan, cycle, "json");

    /**
     * NOTE
     * If the old cycle object is null or empty no need to emit the change because it fresh login
     * This was casing a reload to the home page after each login
     */
    if (cycle.id === oldCycle.id || this._isCycleNullOrEmpty(oldCycle)) return;

    if (!preventDefault) {
      //Reset the tables filters
      this._dataTableService.resetAllTablesState();

      //Reload all active tabs
      this._tabsService.reloadOtherTabs();

      this._cycleHasChanged.next();
    }
  }

  /**
   * @note Called by system layout component to initialize system layout data
   */
  public init() {
    this._initCycles();
    this._initEntities();
    this._initUserPermissions();
  }

  private _initEntities() {
    const savedActiveEntityId = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentEntity, {
      property: "id",
      type: "prop",
    }) as number | undefined;
    this._fetchAllEntities(savedActiveEntityId);
  }

  private _initCycles() {
    const savedActiveCycle = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, {
      type: "json",
    });
    if (savedActiveCycle) {
      this.selectCycle(savedActiveCycle as CycleModelType);
    }
    this._fetchAllCycles();
  }

  private _fetchAllEntities(entityId?: number) {
    this._entitiesApiService.getAll(AdaaBoolean.Y, AdaaBoolean.Y).subscribe({
      next: (res) => {
        if (res.inError) {
          return;
        }

        this.entities.update(() => res.responseData ?? []);

        if (res.responseData?.length > 0) {
          const entity = res.responseData.find((e) => e.id === entityId) ?? res.responseData[0];
          this.selectEntity(entity);
          AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentEntity, entity, "json");
        }
      },
    });
  }

  private _fetchAllCycles() {
    this._cyclesApiService.getAll().subscribe({
      next: (res) => {
        if (res.inError) return;

        const cycles = res.responseData ?? [];

        this.cycles.update(() => cycles);

        const cycle = AdaaHelper.getLocalStorage(Constants.localStorageKeys.currentPlan, {
          type: "json",
        }) as CycleModelType;

        const lastIndex = cycles?.length > 0 ? cycles.length - 1 : null;
        const lastCycle = AdaaHelper.isDefined(lastIndex) ? cycles?.[lastIndex] : null;

        if (!cycle && lastCycle) {
          this.selectCycle(lastCycle);
          AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentPlan, lastCycle, "json");
        } else if (cycle && lastCycle && !cycles.find((c) => c.id === cycle.id)) {
          this.selectCycle(lastCycle);
          AdaaHelper.setLocalStorage(Constants.localStorageKeys.currentPlan, lastCycle, "json");
        }
      },
    });
  }

  private _downloadEntityLogo() {
    const entity = this.activeEntity();
    const fileId = entity?.logoHorizontalAttach?.id ?? entity?.logoVerticalAttach?.id;

    if (fileId)
      this._filesApiService.download(fileId!).subscribe({
        next: async (blob) => {
          const dataUrl = window.URL.createObjectURL(blob);
          this.activeEntityLogo.update(() => dataUrl);
        },
      });
    else this.activeEntityLogo.update(() => undefined);
  }

  private _initUserPermissions() {
    this._permissionService.loadUserPermissions().subscribe();
  }

  private _isCycleNullOrEmpty(cycle: CycleModelType) {
    return !cycle || Object.keys(cycle).length === 0;
  }
}
